// pages/CuentasPagar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import { obtenerFechaLocal } from '../utils/dateUtils';
import { useFiscal } from '../hooks/useFiscal';

const fmtMonto = (n) => new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2 }).format(n || 0);
const fmtFecha = (d) => { if (!d) return '—'; const [y, m, day] = String(d).substring(0, 10).split('-'); return `${day}/${m}/${y}`; };
const NCF_REGEX = /^[BE]\d{2}[\d-]{8,12}$/i;
const validarNcf = (v) => !v || NCF_REGEX.test(v.replace(/[\s.]/g, ''));
const TIPOS_BIEN_SERVICIO_606 = [
  ['01', 'Gastos de personal'],
  ['02', 'Gastos por trabajos, suministros y servicios'],
  ['03', 'Arrendamientos'],
  ['04', 'Gastos de activos fijos'],
  ['05', 'Gastos de representación'],
  ['06', 'Otras deducciones admitidas'],
  ['07', 'Gastos financieros'],
  ['08', 'Gastos extraordinarios'],
  ['09', 'Compras y gastos que formarán parte del costo de venta'],
  ['10', 'Adquisiciones de activos'],
  ['11', 'Gastos de seguros'],
];

export default function CuentasPagar() {
  const navigate = useNavigate();
  const fiscal = useFiscal();

  const [tab,       setTab]       = useState('dashboard');
  const [resumen,       setResumen]       = useState(null);
  const [cuentas,       setCuentas]       = useState([]);   // lista completa (tab Cuentas)
  const [cuentasDash,   setCuentasDash]   = useState([]);   // solo pendiente/parcial (tab Dashboard)
  const [aging,         setAging]         = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,       setModal]       = useState(false);
  const [modalEditar, setModalEditar] = useState(null); // cuenta completa a editar
  const [modalPago,   setModalPago]   = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [ordenes,     setOrdenes]     = useState([]);
  const [tiposRetencionIsr, setTiposRetencionIsr] = useState([]);
  const [catalogoRetencionCargado, setCatalogoRetencionCargado] = useState(false);

  const [filtro, setFiltro] = useState({ estado: '', proveedor_id: '' });
  const [busqueda, setBusqueda] = useState('');

  const [planCuentas, setPlanCuentas] = useState([]);

  const [form, setForm] = useState({
    proveedor_id: '', numero_factura_proveedor: '', ncf_proveedor: '',
    fecha_emision: obtenerFechaLocal(),
    fecha_vencimiento: '', monto_total: '', itbis: '', concepto: '',
    orden_compra_id: '', cuenta_contable_codigo: '', tipo_bien_servicio_606: '',
    tipo_retencion_isr: '', aplica_retencion: false, porcentaje_isr: '', porcentaje_itbis: ''
  });

  const [formEditar, setFormEditar] = useState({});

  const [guardandoCrear,  setGuardandoCrear]  = useState(false);
  const [guardandoEditar, setGuardandoEditar] = useState(false);
  const [guardandoPago,   setGuardandoPago]   = useState(false);
  const [anulandoId,      setAnulandoId]      = useState(null);
  const [clasificandoId, setClasificandoId] = useState(null);

  const [formPago, setFormPago] = useState({
    monto: '', fecha_pago: obtenerFechaLocal(),
    metodo_pago: 'transferencia', referencia: '', banco: '', observaciones: ''
  });

  const proveedorSeleccionado = proveedores.find(p => String(p.id) === String(form.proveedor_id));
  const condicionCrear = proveedorSeleccionado?.condicion_fiscal || 'PENDIENTE_CLASIFICACION';
  const esB11Crear = condicionCrear === 'NO_REGISTRADO';
  const cxpB11Inmutable = Boolean(modalEditar?.ncf_generado_por_sistema);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro.estado)       params.set('estado',       filtro.estado);
      if (filtro.proveedor_id) params.set('proveedor_id', filtro.proveedor_id);
      const [rDash, rCuentas, rAging] = await Promise.all([
        api.get('/cuentas-pagar/dashboard'),
        api.get(`/cuentas-pagar?${params}`),
        api.get('/cuentas-pagar/aging')
      ]);
      setResumen(rDash.data.resumen);
      setCuentasDash(rDash.data.cuentas || []);  // pendiente/parcial para dashboard
      setCuentas(rCuentas.data.cuentas || []);   // lista completa con filtros
      setAging(rAging.data.aging || []);
    } catch (e) {
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    api.get('/ordenes-compra/proveedores').then(r => setProveedores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/ordenes-compra').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      setOrdenes(lista.filter(o => ['aprobada','recibida','cerrada'].includes(o.estado)));
    }).catch(() => {});
    api.get('/contabilidad/cuentas').then(r => {
      const aplanar = (arr) => arr.flatMap(c => [c, ...(c.hijos ? aplanar(c.hijos) : [])]);
      const todas = aplanar(Array.isArray(r.data.cuentas) ? r.data.cuentas : []);
      setPlanCuentas(todas.filter(c => c.acepta_movimientos));
    }).catch(() => {});
    api.get('/cuentas-pagar/catalogos/tipo-retencion-isr').then(r => {
      setTiposRetencionIsr(Array.isArray(r.data?.tipos) ? r.data.tipos : []);
      setCatalogoRetencionCargado(true);
    }).catch(() => setCatalogoRetencionCargado(false));
  }, []);

  const handleGuardar = async () => {
    if (!form.proveedor_id || !form.monto_total || !form.fecha_vencimiento) {
      return Swal.fire('Atención', 'Proveedor, monto y fecha de vencimiento son requeridos', 'warning');
    }
    if (condicionCrear === 'PENDIENTE_CLASIFICACION') {
      return Swal.fire('Atención', 'Clasifique fiscalmente el proveedor antes de registrar una nueva cuenta', 'warning');
    }
    if (esB11Crear && !form.tipo_bien_servicio_606) {
      return Swal.fire('Atención', 'Tipo Bien/Servicio 606 es obligatorio para un proveedor no registrado', 'warning');
    }
    if (guardandoCrear) return;
    const isrPositivo = Number(form.porcentaje_isr || 0) > 0;
    if (isrPositivo && (!catalogoRetencionCargado || tiposRetencionIsr.length === 0)) {
      return Swal.fire('Error', 'No se pudo cargar el catálogo de Tipo de Retención en ISR. No se puede guardar una retención ISR sin catálogo.', 'error');
    }
    if (isrPositivo && !form.tipo_retencion_isr) {
      return Swal.fire('Atención', 'Debe seleccionar el Tipo de Retención en ISR.', 'warning');
    }
    if (form.aplica_retencion) {
      const rates = [form.porcentaje_isr, form.porcentaje_itbis].map(Number);
      if (rates.some(rate => !Number.isFinite(rate) || rate < 0 || rate > 100 || Number(rate.toFixed(2)) !== rate)) {
        return Swal.fire('Atención', 'Los porcentajes deben estar entre 0 y 100 y tener máximo dos decimales', 'warning');
      }
      if (!rates.some(rate => rate > 0)) return Swal.fire('Atención', 'Indica al menos un porcentaje mayor que cero', 'warning');
    }
    setGuardandoCrear(true);
    try {
      const { data } = await api.post('/cuentas-pagar', {
        ...form,
        tipo_retencion_isr: isrPositivo ? form.tipo_retencion_isr : null,
      });
      setModal(false);
      setForm({ proveedor_id: '', numero_factura_proveedor: '', ncf_proveedor: '', fecha_emision: obtenerFechaLocal(), fecha_vencimiento: '', monto_total: '', itbis: '', concepto: '', orden_compra_id: '', cuenta_contable_codigo: '', tipo_bien_servicio_606: '', tipo_retencion_isr: '', aplica_retencion: false, porcentaje_isr: '', porcentaje_itbis: '' });
      cargar();
      Swal.fire({ icon: 'success', title: 'Cuenta registrada', text: data?.cuenta?.ncf_generado_por_sistema ? `B11 generado: ${data.cuenta.ncf_proveedor}` : undefined, timer: 2500, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al guardar', 'error');
    } finally {
      setGuardandoCrear(false);
    }
  };

  const mapearCuentaAFormularioEdicion = (c) => ({
      proveedor_id:             String(c.proveedor_id),
      numero_factura_proveedor: c.numero_factura_proveedor ?? '',
      ncf_proveedor:            c.ncf_proveedor ?? '',
      fecha_emision:            c.fecha_emision ? String(c.fecha_emision).substring(0, 10) : obtenerFechaLocal(),
      fecha_vencimiento:        c.fecha_vencimiento ? String(c.fecha_vencimiento).substring(0, 10) : '',
      monto_total:              parseFloat((parseFloat(c.monto_total || 0) - parseFloat(c.itbis || 0)).toFixed(2)),
      itbis:                    parseFloat(c.itbis || 0),
      concepto:                 c.concepto ?? '',
      orden_compra_id:          c.orden_compra_id ? String(c.orden_compra_id) : '',
      cuenta_contable_codigo:   c.cuenta_contable_codigo ? String(c.cuenta_contable_codigo) : '',
      tipo_bien_servicio_606:   c.tipo_bien_servicio_606 ?? '',
      aplica_retencion:         c.aplica_retencion ?? (parseFloat(c.retencion_total || 0) > 0),
      porcentaje_isr:           c.porcentaje_isr_aplicado ?? '',
      porcentaje_itbis:         c.porcentaje_itbis_aplicado ?? '',
      tipo_retencion_isr:       c.tipo_retencion_isr ?? '',
  });

  const abrirEditar = async (c) => {
    setFormEditar(mapearCuentaAFormularioEdicion(c));
    setModalEditar(c);
    try {
      const { data } = await api.get(`/cuentas-pagar/${c.id}`);
      if (data?.cuenta) {
        setFormEditar(mapearCuentaAFormularioEdicion(data.cuenta));
        setModalEditar(data.cuenta);
      }
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo cargar el detalle de la cuenta', 'error');
    }
  };

  const handleGuardarEditar = async () => {
    if (!formEditar.proveedor_id || !formEditar.monto_total || !formEditar.fecha_vencimiento) {
      return Swal.fire('Atención', 'Proveedor, monto y fecha de vencimiento son requeridos', 'warning');
    }
    if (guardandoEditar) return;
    const isrPositivo = Number(formEditar.porcentaje_isr || 0) > 0;
    if (isrPositivo && (!catalogoRetencionCargado || tiposRetencionIsr.length === 0)) {
      return Swal.fire('Error', 'No se pudo cargar el catálogo de Tipo de Retención en ISR. No se puede guardar una retención ISR sin catálogo.', 'error');
    }
    if (isrPositivo && !formEditar.tipo_retencion_isr) {
      return Swal.fire('Atención', 'Debe seleccionar el Tipo de Retención en ISR.', 'warning');
    }
    if (formEditar.aplica_retencion) {
      const rates = [formEditar.porcentaje_isr, formEditar.porcentaje_itbis].map(Number);
      if (rates.some(rate => !Number.isFinite(rate) || rate < 0 || rate > 100 || Number(rate.toFixed(2)) !== rate)) {
        return Swal.fire('Atención', 'Los porcentajes deben estar entre 0 y 100 y tener máximo dos decimales', 'warning');
      }
      if (!rates.some(rate => rate > 0)) return Swal.fire('Atención', 'Indica al menos un porcentaje mayor que cero', 'warning');
    }
    setGuardandoEditar(true);
    try {
      await api.put(`/cuentas-pagar/${modalEditar.id}`, {
        ...formEditar,
        tipo_retencion_isr: isrPositivo ? formEditar.tipo_retencion_isr : null,
      });
      setModalEditar(null);
      cargar();
      Swal.fire({ icon: 'success', title: 'Cuenta actualizada', timer: 2000, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al actualizar', 'error');
    } finally {
      setGuardandoEditar(false);
    }
  };

  const handlePagar = async () => {
    if (!formPago.monto || parseFloat(formPago.monto) <= 0) return Swal.fire('Atención', 'Ingresa un monto válido', 'warning');
    if (guardandoPago) return;
    setGuardandoPago(true);
    try {
      await api.post(`/cuentas-pagar/${modalPago.id}/pagar`, formPago);
      setModalPago(null);
      cargar();
      Swal.fire({ icon: 'success', title: 'Pago registrado', timer: 1800, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al registrar pago', 'error');
    } finally {
      setGuardandoPago(false);
    }
  };

  const handleAnular = async (cuenta) => {
    if (anulandoId !== null) return;
    const confirmacion = await Swal.fire({
      title: 'Anular cuenta por pagar',
      text: 'La cuenta permanecerá visible como anulada y se generará una reversa contable.',
      input: 'textarea',
      inputLabel: 'Motivo de anulación',
      inputPlaceholder: 'Escribe el motivo...',
      inputValidator: (value) => !value?.trim() ? 'El motivo es obligatorio' : undefined,
      showCancelButton: true,
      confirmButtonText: 'Anular',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });
    if (!confirmacion.isConfirmed) return;
    setAnulandoId(cuenta.id);
    try {
      await api.post(`/cuentas-pagar/${cuenta.id}/anular`, { motivo: confirmacion.value.trim() });
      cargar();
      Swal.fire({ icon: 'success', title: 'Cuenta anulada', timer: 1800, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al anular la cuenta', 'error');
    } finally {
      setAnulandoId(null);
    }
  };

  const handleClasificarTipoRetencion = async (cuenta) => {
    if (clasificandoId !== null) return;
    if (!catalogoRetencionCargado || tiposRetencionIsr.length === 0) {
      return Swal.fire('Error', 'No se pudo cargar el catálogo de Tipo de Retención en ISR.', 'error');
    }
    const inputOptions = Object.fromEntries(
      tiposRetencionIsr.map(tipo => [tipo.codigo, `${tipo.codigo} — ${tipo.nombre}`])
    );
    const seleccion = await Swal.fire({
      title: 'Clasificar Tipo de Retención ISR',
      text: `La clasificación de ${cuenta.numero_factura_proveedor || cuenta.ncf_proveedor || `CxP ${cuenta.id}`} sólo modifica metadata fiscal del 606.`,
      input: 'select',
      inputOptions,
      inputPlaceholder: 'Seleccionar tipo',
      inputValue: '',
      inputValidator: value => !value ? 'Seleccione un tipo de retención ISR' : undefined,
      showCancelButton: true,
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar',
    });
    if (!seleccion.isConfirmed) return;

    const motivo = await Swal.fire({
      title: 'Motivo de clasificación',
      input: 'textarea',
      inputPlaceholder: 'Indique el soporte contable de la clasificación...',
      inputValidator: value => !value?.trim() ? 'El motivo es obligatorio' : undefined,
      showCancelButton: true,
      confirmButtonText: 'Registrar clasificación',
      cancelButtonText: 'Cancelar',
    });
    if (!motivo.isConfirmed) return;

    setClasificandoId(cuenta.id);
    try {
      await api.post(`/cuentas-pagar/${cuenta.id}/corregir-dato-fiscal`, {
        campo: 'tipo_retencion_isr',
        valor_nuevo: seleccion.value,
        motivo: motivo.value.trim(),
      });
      await cargar();
      Swal.fire({ icon: 'success', title: 'Clasificación registrada', timer: 1800, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo registrar la clasificación fiscal', 'error');
    } finally {
      setClasificandoId(null);
    }
  };

  const calcularRetencionPreview = (datos) => {
    const subtotal = parseFloat(datos.monto_total) || 0;
    const itbis = parseFloat(datos.itbis) || 0;
    if (!datos.aplica_retencion) {
      return { isr: 0, itbisRetenido: 0, total: 0, neto: subtotal + itbis };
    }
    const isr = Math.round(subtotal * (Number(datos.porcentaje_isr || 0) / 100) * 100) / 100;
    const itbisRetenido = Math.round(itbis * (Number(datos.porcentaje_itbis || 0) / 100) * 100) / 100;
    const total = Math.round((isr + itbisRetenido) * 100) / 100;
    return { isr, itbisRetenido, total, neto: Math.max(0, Math.round((subtotal + itbis - total) * 100) / 100) };
  };

  const retencionCrear = calcularRetencionPreview(form);
  const retencionEditar = calcularRetencionPreview(formEditar);

  const cuentasFiltradas = cuentas.filter(c => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return true;
    return [
      c.proveedor?.nombre,
      c.numero_factura_proveedor,
      c.ncf_proveedor,
      c.concepto,
    ].some(v => String(v || '').toLowerCase().includes(q));
  });

  const requiereClasificacion = (cuenta) => (
    Number(cuenta.retencion_isr || 0) > 0
    && !tiposRetencionIsr.some(tipo => String(tipo.codigo) === String(cuenta.tipo_retencion_isr || ''))
  );

  const badgeEstado = (e, dias) => {
    const vencida = dias > 0;
    if (e === 'pagada')  return <span style={{ ...sx.badge, background: '#d1fae5', color: '#065f46' }}>Pagada</span>;
    if (e === 'anulada') return <span style={{ ...sx.badge, background: '#fee2e2', color: '#991b1b' }}>Anulada</span>;
    if (vencida)         return <span style={{ ...sx.badge, background: '#fee2e2', color: '#991b1b' }}>Vencida {dias}d</span>;
    if (e === 'parcial') return <span style={{ ...sx.badge, background: '#fef3c7', color: '#92400e' }}>Parcial</span>;
    return <span style={{ ...sx.badge, background: '#dbeafe', color: '#1e40af' }}>Pendiente</span>;
  };

  return (
    <div style={sx.page}>
      {/* Header */}
      <div style={sx.header}>
        <div>
          <button onClick={() => navigate('/ordenes-compra')} style={sx.btnVolver}>← Volver</button>
          <h1 style={sx.titulo}>Cuentas por Pagar</h1>
          <p style={sx.subtitulo}>Gestión de facturas de proveedores, pagos y aging</p>
        </div>
        <button onClick={() => setModal(true)} style={sx.btnPrimario}>+ Nueva Cuenta</button>
      </div>

      {/* Tabs */}
      <div style={sx.tabsBar}>
        {[
          { key: 'dashboard', label: '📊 Dashboard' },
          { key: 'cuentas',   label: '📋 Cuentas' },
          { key: 'aging',     label: '⏳ Aging por Proveedor' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ ...sx.tabBtn, ...(tab === t.key ? sx.tabActivo : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#6c757d' }}>⏳ Cargando...</div>
      ) : (
        <>
          {/* ── Dashboard ── */}
          {tab === 'dashboard' && resumen && (
            <div>
              <div style={sx.kpiGrid}>
                <KpiCard icono="💰" titulo="Total Pendiente"  valor={`RD$ ${fmtMonto(resumen.total_pendiente)}`} color="#dc2626" bg="#fef2f2" />
                <KpiCard icono="🔴" titulo="Vencidas"         valor={`RD$ ${fmtMonto(resumen.vencidas)}`}        color="#991b1b" bg="#fff1f2" />
                <KpiCard icono="⚠" titulo="Vencen en 7 días" valor={`RD$ ${fmtMonto(resumen.por_vencer_7)}`}    color="#f59e0b" bg="#fffbeb" />
                <KpiCard icono="📋" titulo="Facturas abiertas" valor={resumen.count_facturas}                    color="#0066cc" bg="#eff6ff" />
              </div>

              {/* Aging gráfico */}
              <div style={sx.card}>
                <h3 style={sx.cardTitulo}>⏳ Análisis de Aging</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
                  {[
                    { label: 'Corriente',    valor: resumen.aging.corriente, color: '#059669' },
                    { label: '1-30 días',    valor: resumen.aging.d30,       color: '#f59e0b' },
                    { label: '31-60 días',   valor: resumen.aging.d60,       color: '#f97316' },
                    { label: '61-90 días',   valor: resumen.aging.d90,       color: '#dc2626' },
                    { label: '> 90 días',    valor: resumen.aging.mas90,     color: '#7f1d1d' },
                  ].map(a => (
                    <div key={a.label} style={{ background: '#f8f9fa', borderRadius: 8, padding: '1rem', textAlign: 'center', borderTop: `4px solid ${a.color}` }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{a.label}</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: a.valor > 0 ? a.color : '#1a1a2e' }}>RD$ {fmtMonto(a.valor)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabla vencidas */}
              <div style={sx.card}>
                <h3 style={sx.cardTitulo}>🔴 Cuentas Vencidas</h3>
                <TablaCompacta
                  cuentas={cuentasDash.filter(c => c.dias_vencida > 0)}
                  onPagar={(cuenta) => { setModalPago(cuenta); setFormPago(f => ({ ...f, monto: parseFloat(cuenta.saldo_pendiente || 0) })); }}
                  onEditar={abrirEditar}
                  onAnular={handleAnular}
                  onClasificar={handleClasificarTipoRetencion}
                  requiereClasificacion={requiereClasificacion}
                  badgeEstado={badgeEstado}
                />
              </div>
            </div>
          )}

          {/* ── Cuentas ── */}
          {tab === 'cuentas' && (
            <div>
              <div style={sx.filtrosRow}>
                <input
                  type="text"
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  placeholder="🔎 Buscar por proveedor, N° factura, NCF o concepto..."
                  style={{ ...sx.select, minWidth: 300, flex: 1, cursor: 'text' }}
                />
                <select value={filtro.proveedor_id} onChange={e => setFiltro(f => ({ ...f, proveedor_id: e.target.value }))} style={sx.select}>
                  <option value="">Todos los proveedores</option>
                  {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
                <select value={filtro.estado} onChange={e => setFiltro(f => ({ ...f, estado: e.target.value }))} style={sx.select}>
                  <option value="">Todos los estados</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="parcial">Parcial</option>
                  <option value="pagada">Pagada</option>
                </select>
                <button onClick={cargar} style={sx.btnSecundario}>🔄 Actualizar</button>
              </div>
              <div style={sx.tablaWrap}>
                {cuentas.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📭</div>
                    <p style={{ color: '#6c757d', fontWeight: 600 }}>No hay cuentas por pagar</p>
                    <button onClick={() => setModal(true)} style={{ ...sx.btnPrimario, marginTop: '0.75rem' }}>+ Registrar cuenta</button>
                  </div>
                ) : cuentasFiltradas.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔍</div>
                    <p style={{ color: '#6c757d', fontWeight: 600 }}>Sin resultados para "{busqueda}"</p>
                    <button onClick={() => setBusqueda('')} style={{ ...sx.btnSecundario, marginTop: '0.75rem' }}>Limpiar búsqueda</button>
                  </div>
                ) : (
                  <TablaCompacta
                    cuentas={cuentasFiltradas}
                    onPagar={(cuenta) => { setModalPago(cuenta); setFormPago(f => ({ ...f, monto: parseFloat(cuenta.saldo_pendiente || 0) })); }}
                    onEditar={abrirEditar}
                    onAnular={handleAnular}
                    onClasificar={handleClasificarTipoRetencion}
                    requiereClasificacion={requiereClasificacion}
                    badgeEstado={badgeEstado}
                    completa
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Aging ── */}
          {tab === 'aging' && (
            <div style={sx.tablaWrap}>
              {aging.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>Sin datos de aging</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Proveedor','Corriente','1-30 días','31-60 días','61-90 días','> 90 días','Total'].map(h => (
                        <th key={h} style={{ ...sx.th, textAlign: h === 'Proveedor' ? 'left' : 'right' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {aging.map((a, i) => (
                      <tr key={a.proveedor_id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                        <td style={{ ...sx.td, fontWeight: 600 }}>{a.nombre}</td>
                        <td style={{ ...sx.td, color: '#059669', textAlign: 'right' }}>{fmtMonto(a.corriente)}</td>
                        <td style={{ ...sx.td, color: '#f59e0b', textAlign: 'right' }}>{fmtMonto(a.d30)}</td>
                        <td style={{ ...sx.td, color: '#f97316', textAlign: 'right' }}>{fmtMonto(a.d60)}</td>
                        <td style={{ ...sx.td, color: '#dc2626', textAlign: 'right' }}>{fmtMonto(a.d90)}</td>
                        <td style={{ ...sx.td, color: '#7f1d1d', textAlign: 'right' }}>{fmtMonto(a.mas90)}</td>
                        <td style={{ ...sx.td, fontWeight: 700, textAlign: 'right' }}>RD$ {fmtMonto(a.total)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#1e3a8a' }}>
                      <td style={{ ...sx.td, color: '#fff', fontWeight: 700 }}>TOTAL</td>
                      {['corriente','d30','d60','d90','mas90'].map(k => (
                        <td key={k} style={{ ...sx.td, color: '#fff', fontWeight: 700, textAlign: 'right' }}>
                          {fmtMonto(aging.reduce((s, a) => s + (a[k] || 0), 0))}
                        </td>
                      ))}
                      <td style={{ ...sx.td, color: '#fff', fontWeight: 700, textAlign: 'right' }}>
                        RD$ {fmtMonto(aging.reduce((s, a) => s + (a.total || 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Modal nueva cuenta ── */}
      {modal && (
        <div style={sx.overlay}>
          <div style={sx.modalBox}>
            <div style={sx.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>💳 Nueva Cuenta por Pagar</h2>
              <button onClick={() => setModal(false)} style={sx.btnCerrar}>✕</button>
            </div>
            <div style={sx.modalBody}>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Proveedor *</label>
                  <select value={form.proveedor_id} onChange={e => setForm(f => ({ ...f, proveedor_id: e.target.value, ncf_proveedor: '' }))} style={sx.input}>
                    <option value="">Seleccionar proveedor</option>
                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                  {proveedorSeleccionado?.condicion_fiscal === 'PENDIENTE_CLASIFICACION' && <small style={{ color: '#b45309' }}>Proveedor pendiente de clasificación fiscal. Clasifíquelo antes de registrar la CxP.</small>}
                  {esB11Crear && <small style={{ color: '#1e40af' }}>El comprobante B11 será generado automáticamente al registrar la cuenta por pagar.</small>}
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Orden de Compra (opcional)</label>
                  <select value={form.orden_compra_id} onChange={e => setForm(f => ({ ...f, orden_compra_id: e.target.value }))} style={sx.input}>
                    <option value="">Sin OC</option>
                    {ordenes.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.proveedor?.nombre || 'Sin proveedor'} — RD$ {fmtMonto(o.total)}</option>)}
                  </select>
                </div>
              </div>
              <div style={sx.campo}>
                  <label style={sx.label}>Tipo Bien/Servicio 606 {esB11Crear && '*'}</label>
                <select
                  value={form.tipo_bien_servicio_606}
                  onChange={e => setForm(f => ({ ...f, tipo_bien_servicio_606: e.target.value }))}
                  style={sx.input}
                >
                  <option value="">Pendiente de clasificación contable</option>
                  {TIPOS_BIEN_SERVICIO_606.map(([codigo, nombre]) => (
                    <option key={codigo} value={codigo}>{codigo} — {nombre}</option>
                  ))}
                </select>
                <small style={{ color: '#6c757d', fontSize: '0.78rem' }}>
                  Obligatorio para exportar esta factura en el formato DGII 606.
                </small>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>N° Factura Proveedor</label>
                  <input type="text" value={form.numero_factura_proveedor} onChange={e => setForm(f => ({ ...f, numero_factura_proveedor: e.target.value }))} style={sx.input} placeholder="Ej: FAC-001" />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>NCF del Proveedor</label>
                  <input type="text" value={form.ncf_proveedor} disabled={esB11Crear || condicionCrear === 'PENDIENTE_CLASIFICACION'} onChange={e => setForm(f => ({ ...f, ncf_proveedor: e.target.value }))} style={{ ...sx.input, borderColor: form.ncf_proveedor && !validarNcf(form.ncf_proveedor) ? '#f59e0b' : undefined }} placeholder={esB11Crear ? 'Generado por eFIT' : 'Ej: B0100000001'} />
                  {form.ncf_proveedor && !validarNcf(form.ncf_proveedor) && (
                    <small style={{ color: '#b45309', fontSize: '0.78rem' }}>Formato esperado: B0100000001 o E320009640005</small>
                  )}
                </div>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Fecha Factura</label>
                  <input type="date" value={form.fecha_emision} onChange={e => setForm(f => ({ ...f, fecha_emision: e.target.value }))} style={sx.input} />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Fecha Vencimiento *</label>
                  <input type="date" value={form.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} style={sx.input} />
                </div>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Subtotal (sin ITBIS) *</label>
                  <input type="number" step="0.01" value={form.monto_total} onChange={e => setForm(f => ({ ...f, monto_total: e.target.value }))} style={sx.input} placeholder="0.00" />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>{fiscal.impuesto_label || 'ITBIS'}</label>
                  <input type="number" step="0.01" value={form.itbis} onChange={e => setForm(f => ({ ...f, itbis: e.target.value }))} style={sx.input} placeholder="0.00" />
                </div>
              </div>
              <div style={sx.campo}>
                <label style={{ ...sx.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.aplica_retencion} onChange={e => setForm(f => ({ ...f, aplica_retencion: e.target.checked, ...(e.target.checked ? {} : { porcentaje_isr: '', porcentaje_itbis: '', tipo_retencion_isr: '' }) }))} />
                  ¿Esta factura lleva retención?
                </label>
              </div>
              {form.aplica_retencion && (
                <div style={sx.fila2col}>
                  <div style={sx.campo}><label style={sx.label}>Retención ISR (%)</label><input type="number" min="0" max="100" step="0.01" value={form.porcentaje_isr} onChange={e => setForm(f => ({ ...f, porcentaje_isr: e.target.value, ...(Number(e.target.value || 0) > 0 ? {} : { tipo_retencion_isr: '' }) }))} style={sx.input} placeholder="0.00" /></div>
                  <div style={sx.campo}><label style={sx.label}>Retención ITBIS (%)</label><input type="number" min="0" max="100" step="0.01" value={form.porcentaje_itbis} onChange={e => setForm(f => ({ ...f, porcentaje_itbis: e.target.value }))} style={sx.input} placeholder="0.00" /></div>
                  {Number(form.porcentaje_isr || 0) > 0 && (
                    <div style={sx.campo}>
                      <label style={sx.label}>Tipo de Retención ISR *</label>
                      <select
                        value={form.tipo_retencion_isr || ''}
                        onChange={e => setForm(f => ({ ...f, tipo_retencion_isr: e.target.value }))}
                        style={sx.input}
                      >
                        <option value="">Seleccionar tipo de retención ISR</option>
                        {tiposRetencionIsr.map(tipo => (
                          <option key={tipo.codigo} value={tipo.codigo}>{tipo.codigo} — {tipo.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              {(parseFloat(form.monto_total) > 0 || parseFloat(form.itbis) > 0) && (
                <div style={{ background: '#eff6ff', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                  <ResumenRetencion subtotal={form.monto_total} itbis={form.itbis} retencion={retencionCrear} />
                </div>
              )}
              <div style={sx.campo}>
                <label style={sx.label}>Concepto</label>
                <textarea value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} style={{ ...sx.input, minHeight: 70, resize: 'vertical' }} placeholder="Descripción de la compra..." />
              </div>
              {planCuentas.length > 0 && (
                <div style={sx.campo}>
                  <label style={sx.label}>Cuenta a afectar <span style={{ fontWeight: 400, color: '#6c757d' }}>(opcional — genera asiento automático)</span></label>
                  <select value={form.cuenta_contable_codigo} onChange={e => setForm(f => ({ ...f, cuenta_contable_codigo: e.target.value }))} style={sx.input}>
                    <option value="">Sin asiento contable</option>
                    {planCuentas.map(c => (
                      <option key={c.id} value={c.codigo}>{c.codigo} — {c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div style={sx.modalFooter}>
              <button onClick={() => setModal(false)} style={sx.btnSecundario}>Cancelar</button>
              <button
                onClick={handleGuardar}
                disabled={guardandoCrear}
                style={{ ...sx.btnPrimario, opacity: guardandoCrear ? 0.6 : 1, cursor: guardandoCrear ? 'not-allowed' : 'pointer' }}
              >
                {guardandoCrear ? '⏳ Guardando...' : '💾 Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal editar cuenta ── */}
      {modalEditar && (
        <div style={sx.overlay}>
          <div style={sx.modalBox}>
            <div style={sx.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>✏️ Editar Cuenta por Pagar</h2>
              <button onClick={() => setModalEditar(null)} style={sx.btnCerrar}>✕</button>
            </div>
            <div style={sx.modalBody}>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Proveedor *</label>
                  <select value={formEditar.proveedor_id} disabled={cxpB11Inmutable} onChange={e => setFormEditar(f => ({ ...f, proveedor_id: e.target.value }))} style={sx.input}>
                    <option value="">Seleccionar proveedor</option>
                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                  {cxpB11Inmutable && <small style={{ color: '#1e40af' }}>Proveedor bloqueado: B11 emitido por eFIT.</small>}
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Orden de Compra (opcional)</label>
                  <select value={formEditar.orden_compra_id} onChange={e => setFormEditar(f => ({ ...f, orden_compra_id: e.target.value }))} style={sx.input}>
                    <option value="">Sin OC</option>
                    {ordenes.map(o => <option key={o.id} value={o.id}>{o.numero} — {o.proveedor?.nombre || ''} — RD$ {fmtMonto(o.total)}</option>)}
                  </select>
                </div>
              </div>
              <div style={sx.campo}>
                <label style={sx.label}>Tipo Bien/Servicio 606</label>
                <select
                  value={formEditar.tipo_bien_servicio_606}
                  disabled={cxpB11Inmutable}
                  onChange={e => setFormEditar(f => ({ ...f, tipo_bien_servicio_606: e.target.value }))}
                  style={sx.input}
                >
                  <option value="">Pendiente de clasificación contable</option>
                  {TIPOS_BIEN_SERVICIO_606.map(([codigo, nombre]) => (
                    <option key={codigo} value={codigo}>{codigo} — {nombre}</option>
                  ))}
                </select>
                <small style={{ color: '#6c757d', fontSize: '0.78rem' }}>
                  {cxpB11Inmutable
                    ? 'Un B11 emitido por eFIT debe conservar su clasificación 606.'
                    : 'Obligatorio para exportar esta factura en el formato DGII 606.'}
                </small>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>N° Factura Proveedor</label>
                  <input type="text" value={formEditar.numero_factura_proveedor} onChange={e => setFormEditar(f => ({ ...f, numero_factura_proveedor: e.target.value }))} style={sx.input} />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>NCF del Proveedor</label>
                  <input type="text" value={formEditar.ncf_proveedor} disabled={cxpB11Inmutable} onChange={e => setFormEditar(f => ({ ...f, ncf_proveedor: e.target.value }))} style={{ ...sx.input, borderColor: formEditar.ncf_proveedor && !validarNcf(formEditar.ncf_proveedor) ? '#f59e0b' : undefined }} />
                  {cxpB11Inmutable && <small style={{ color: '#1e40af' }}>B11 emitido por eFIT; no puede reemplazarse.</small>}
                  {formEditar.ncf_proveedor && !validarNcf(formEditar.ncf_proveedor) && (
                    <small style={{ color: '#b45309', fontSize: '0.78rem' }}>Formato esperado: B0100000001 o E320009640005</small>
                  )}
                </div>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Fecha Factura</label>
                  <input type="date" value={formEditar.fecha_emision} onChange={e => setFormEditar(f => ({ ...f, fecha_emision: e.target.value }))} style={sx.input} />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Fecha Vencimiento *</label>
                  <input type="date" value={formEditar.fecha_vencimiento} onChange={e => setFormEditar(f => ({ ...f, fecha_vencimiento: e.target.value }))} style={sx.input} />
                </div>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Subtotal (sin ITBIS) *</label>
                  <input type="number" step="0.01" value={formEditar.monto_total} onChange={e => setFormEditar(f => ({ ...f, monto_total: e.target.value }))} style={sx.input} />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>{fiscal.impuesto_label || 'ITBIS'}</label>
                  <input type="number" step="0.01" value={formEditar.itbis} onChange={e => setFormEditar(f => ({ ...f, itbis: e.target.value }))} style={sx.input} />
                </div>
              </div>
              <div style={sx.campo}>
                <label style={{ ...sx.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!formEditar.aplica_retencion} onChange={e => setFormEditar(f => ({ ...f, aplica_retencion: e.target.checked, ...(e.target.checked ? {} : { porcentaje_isr: '', porcentaje_itbis: '', tipo_retencion_isr: '' }) }))} />
                  ¿Esta factura lleva retención?
                </label>
              </div>
              {formEditar.aplica_retencion && (
                <div style={sx.fila2col}>
                  <div style={sx.campo}><label style={sx.label}>Retención ISR (%)</label><input type="number" min="0" max="100" step="0.01" value={formEditar.porcentaje_isr} onChange={e => setFormEditar(f => ({ ...f, porcentaje_isr: e.target.value, ...(Number(e.target.value || 0) > 0 ? {} : { tipo_retencion_isr: '' }) }))} style={sx.input} placeholder="0.00" /></div>
                  <div style={sx.campo}><label style={sx.label}>Retención ITBIS (%)</label><input type="number" min="0" max="100" step="0.01" value={formEditar.porcentaje_itbis} onChange={e => setFormEditar(f => ({ ...f, porcentaje_itbis: e.target.value }))} style={sx.input} placeholder="0.00" /></div>
                  {Number(formEditar.porcentaje_isr || 0) > 0 && (
                    <div style={sx.campo}>
                      <label style={sx.label}>Tipo de Retención ISR *</label>
                      <select
                        value={formEditar.tipo_retencion_isr || ''}
                        onChange={e => setFormEditar(f => ({ ...f, tipo_retencion_isr: e.target.value }))}
                        style={sx.input}
                      >
                        <option value="">Seleccionar tipo de retención ISR</option>
                        {tiposRetencionIsr.map(tipo => (
                          <option key={tipo.codigo} value={tipo.codigo}>{tipo.codigo} — {tipo.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              {(parseFloat(formEditar.monto_total) > 0 || parseFloat(formEditar.itbis) > 0) && (
                <div style={{ background: '#eff6ff', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
                  <ResumenRetencion subtotal={formEditar.monto_total} itbis={formEditar.itbis} retencion={retencionEditar} />
                </div>
              )}
              <div style={sx.campo}>
                <label style={sx.label}>Concepto</label>
                <textarea value={formEditar.concepto} onChange={e => setFormEditar(f => ({ ...f, concepto: e.target.value }))} style={{ ...sx.input, minHeight: 70, resize: 'vertical' }} />
              </div>
              {planCuentas.length > 0 && (
                <div style={sx.campo}>
                  <label style={sx.label}>Cuenta a afectar <span style={{ fontWeight: 400, color: '#6c757d' }}>(opcional — genera asiento si aún no existe)</span></label>
                  <select value={formEditar.cuenta_contable_codigo} onChange={e => setFormEditar(f => ({ ...f, cuenta_contable_codigo: e.target.value }))} style={sx.input}>
                    <option value="">Sin asiento contable</option>
                    {planCuentas.map(c => (
                      <option key={c.id} value={c.codigo}>{c.codigo} — {c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div style={sx.modalFooter}>
              <button onClick={() => setModalEditar(null)} style={sx.btnSecundario}>Cancelar</button>
              <button
                onClick={handleGuardarEditar}
                disabled={guardandoEditar}
                style={{ ...sx.btnPrimario, opacity: guardandoEditar ? 0.6 : 1, cursor: guardandoEditar ? 'not-allowed' : 'pointer' }}
              >
                {guardandoEditar ? '⏳ Guardando...' : '💾 Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal pago ── */}
      {modalPago && (
        <div style={sx.overlay}>
          <div style={{ ...sx.modalBox, maxWidth: 500 }}>
            <div style={sx.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>💸 Registrar Pago</h2>
              <button onClick={() => setModalPago(null)} style={sx.btnCerrar}>✕</button>
            </div>
            <div style={sx.modalBody}>
              <div style={{ background: '#eff6ff', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', color: '#1e40af' }}>
                <div>Factura bruta: <strong>RD$ {fmtMonto(modalPago.monto_total)}</strong></div>
                <div>Retención ISR: <strong>RD$ {fmtMonto(modalPago.retencion_isr)}</strong></div>
                <div>Retención ITBIS: <strong>RD$ {fmtMonto(modalPago.retencion_itbis)}</strong></div>
                <div>Total retenido: <strong>RD$ {fmtMonto(modalPago.retencion_total)}</strong></div>
                <div>Neto pendiente a pagar: <strong>RD$ {fmtMonto(modalPago.saldo_pendiente)}</strong></div>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Monto *</label>
                  <input type="number" step="0.01" max={modalPago.saldo_pendiente} value={formPago.monto} onChange={e => setFormPago(f => ({ ...f, monto: e.target.value }))} style={sx.input} />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Fecha</label>
                  <input type="date" value={formPago.fecha_pago} onChange={e => setFormPago(f => ({ ...f, fecha_pago: e.target.value }))} style={sx.input} />
                </div>
              </div>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Método</label>
                  <select value={formPago.metodo_pago} onChange={e => setFormPago(f => ({ ...f, metodo_pago: e.target.value }))} style={sx.input}>
                    <option value="transferencia">Transferencia</option>
                    <option value="cheque">Cheque</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select>
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Referencia</label>
                  <input type="text" value={formPago.referencia} onChange={e => setFormPago(f => ({ ...f, referencia: e.target.value }))} style={sx.input} placeholder="No. transacción..." />
                </div>
              </div>
              <div style={sx.campo}>
                <label style={sx.label}>Banco</label>
                <input type="text" value={formPago.banco} onChange={e => setFormPago(f => ({ ...f, banco: e.target.value }))} style={sx.input} placeholder="Ej: BHD León" />
              </div>
            </div>
            <div style={sx.modalFooter}>
              <button onClick={() => setModalPago(null)} style={sx.btnSecundario}>Cancelar</button>
              <button
                onClick={handlePagar}
                disabled={guardandoPago}
                style={{ ...sx.btnPrimario, opacity: guardandoPago ? 0.6 : 1, cursor: guardandoPago ? 'not-allowed' : 'pointer' }}
              >
                {guardandoPago ? '⏳ Registrando...' : '✅ Registrar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TablaCompacta({ cuentas, onPagar, onEditar, onAnular, onClasificar, requiereClasificacion, badgeEstado, completa }) {
  const cols = completa
    ? ['Proveedor','Factura','Vencimiento','Concepto','Total','Retención','Neto','Saldo','Estado','Acciones']
    : ['Proveedor','Vencimiento','Saldo','Estado','Acción'];

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
      <thead>
        <tr>{cols.map(h => <th key={h} style={{ ...sx.th, textAlign: ['Total','Retención','Neto','Saldo'].includes(h) ? 'right' : 'left' }}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {cuentas.map((c, i) => (
          <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
            <td style={{ ...sx.td, fontWeight: 600 }}>{c.proveedor?.nombre || '—'}</td>
            {completa && <td style={sx.td}>{c.numero_factura_proveedor || '—'}</td>}
            <td style={sx.td}>{fmtFecha(c.fecha_vencimiento)}</td>
            {completa && <td style={{ ...sx.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.concepto || '—'}</td>}
            {completa && <td style={{ ...sx.td, textAlign: 'right' }}>RD$ {fmtMonto(c.monto_total)}</td>}
            {completa && <td style={{ ...sx.td, textAlign: 'right', color: parseFloat(c.retencion_total || 0) > 0 ? '#b45309' : '#64748b' }}>RD$ {fmtMonto(c.retencion_total)}</td>}
            {completa && <td style={{ ...sx.td, textAlign: 'right' }}>RD$ {fmtMonto(c.monto_neto_pagar ?? c.monto_total)}</td>}
            <td style={{ ...sx.td, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>RD$ {fmtMonto(c.saldo_pendiente)}</td>
            <td style={sx.td}>{badgeEstado(c.estado, c.dias_vencida)}</td>
            <td style={{ ...sx.td, whiteSpace: 'nowrap' }}>
              {onClasificar && requiereClasificacion?.(c) && c.estado !== 'anulada' && (
                <button onClick={() => onClasificar(c)} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, marginRight: 6 }}>
                  🧾 Clasificar ISR
                </button>
              )}
              {c.estado !== 'pagada' && c.estado !== 'anulada' && (
                <>
                  <button onClick={() => onPagar(c)} style={{ background: '#0066cc', color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, marginRight: 6 }}>
                    💸 Pagar
                  </button>
                  <button onClick={() => onEditar(c)} style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                    ✏️ Editar
                  </button>
                  {c.estado === 'pendiente' && (
                    <button onClick={() => onAnular(c)} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, marginLeft: 6 }}>
                      ⛔ Anular
                    </button>
                  )}
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ResumenRetencion({ subtotal, itbis, retencion }) {
  const bruto = (parseFloat(subtotal) || 0) + (parseFloat(itbis) || 0);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.45rem 1rem', color: '#1e40af', fontSize: '0.88rem' }}>
      <span>Bruto: <strong>RD$ {fmtMonto(bruto)}</strong></span>
      <span>ISR retenido: <strong>RD$ {fmtMonto(retencion.isr)}</strong></span>
      <span>ITBIS retenido: <strong>RD$ {fmtMonto(retencion.itbisRetenido)}</strong></span>
      <span>Total retenido: <strong>RD$ {fmtMonto(retencion.total)}</strong></span>
      <span>Neto a pagar: <strong>RD$ {fmtMonto(retencion.neto)}</strong></span>
    </div>
  );
}

function KpiCard({ icono, titulo, valor, color, bg }) {
  return (
    <div style={{ background: bg, borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{icono}</div>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{titulo}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', marginTop: '0.25rem' }}>{valor}</div>
    </div>
  );
}

const sx = {
  page: { padding: '2rem', maxWidth: 1300, margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' },
  titulo: { fontSize: '2rem', fontWeight: 700, color: '#1a1a2e', margin: '0.5rem 0 0.25rem 0' },
  subtitulo: { color: '#6c757d', fontSize: '0.95rem', margin: 0 },
  btnVolver: { background: 'none', border: 'none', color: '#0066cc', cursor: 'pointer', fontSize: '0.9rem', padding: 0, fontWeight: 600 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  card: { background: '#fff', borderRadius: 10, padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' },
  cardTitulo: { fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 1rem 0' },
  filtrosRow: { display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'center' },
  select: { padding: '0.55rem 0.9rem', border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.9rem', background: '#fff', cursor: 'pointer' },
  btnPrimario: { background: '#0066cc', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.4rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnSecundario: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' },
  tabsBar: { display: 'flex', gap: '0.5rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem' },
  tabBtn: { background: 'none', border: 'none', borderBottom: '3px solid transparent', padding: '0.7rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', color: '#6c757d', marginBottom: -2, transition: 'all 0.15s' },
  tabActivo: { color: '#0066cc', borderBottomColor: '#0066cc' },
  tablaWrap: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflowX: 'auto' },
  th: { background: '#f8f9fa', padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' },
  td: { padding: '0.7rem 1rem', color: '#495057', borderBottom: '1px solid #f1f3f5', fontSize: '0.875rem' },
  badge: { display: 'inline-block', padding: '0.25rem 0.65rem', borderRadius: 50, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modalBox: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  modalBody: { padding: '1.5rem' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' },
  btnCerrar: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6c757d' },
  fila2col: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' },
  campo: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontSize: '0.875rem', fontWeight: 600, color: '#495057' },
  input: { padding: '0.6rem 0.75rem', border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }
};
